import { Button, Col, Container, Form, Row, Table } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { getPageName, getSuccessMessage, getUiInterface } from '../../../locales/translationUtils';
import { useTranslation } from 'react-i18next';
import ConfirmDeleteModal from '../../../components/ConfirmDeleteModal';
import {
  getCargoById,
  CargoResponse,
  deleteCagro,
} from '../../../services/cargoService';
import { useEffect, useState } from 'react';
import NavBar, { NavBarItem } from '../../../components/NavBar';
import LoadingOverlay from '../../../components/Loading';
import { Content } from '../../Release/types';
import { downloadContent } from '../../../services/contentService';
import { Box } from '@mui/material';
import AlertOverlay from '../../../components/Alert';

type ParamType = {
  id: string;
};

export const ViewCargoById: React.FC = () => {
  const { t } = useTranslation();
  const [alert, setAlert] = useState<{
      message: string;
      variant: "success" | "danger" | "warning" | "info";
  } | null>(null);
  const [cargo, setCargo] = useState<CargoResponse | null>(null);
  const { id } = useParams<ParamType>();
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await getCargoById(Number(id));
        setCargo(response.data || null);
      } catch (error) {
        console.error('Erro ao buscar cargo:', error);
      }
      setLoading(false);
    };

    fetchData();
  }, [id]);
      
  const handleDelete = async () => {
      if (!id) return;

      try {
          await deleteCagro(Number(id));

          // Se não houver erro (status 200), considerar sucesso
          const successMessage = getSuccessMessage("deleteInfoCargo", { id: id });
          navigate(`/cargo-info`, {
              state: {
                  message: successMessage,
                  variant: "success",
              },
          });
      } catch (error) {
          setAlert({ message: t('branch.messages.errorDeleting'), variant: "danger" });
      }
  };
  
  const download = async (content: Content) => {
      try {
        const response = await downloadContent(content.id);
        
        const downloadUrl = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `${content.description}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
          setAlert({ message: `${t('cargo.messages.downloadError')} ${content.description}`, variant: "danger" });
      }
  }

  const navItems: NavBarItem[] = [
    { label: t('cargo.labels.home'), route: '/', iconName: 'house-door' },
    {
      label: t('cargo.labels.cargoList'),
      route: '/cargo-info',
      iconName: 'table',
    },
    {
      label: t('cargo.labels.newCargoInfo'),
      route: '/cargo-info/create',
      iconName: 'database-add',
    },
  ];

  return (
    <Container fluid>
      <LoadingOverlay isLoading={loading} />
      <NavBar items={navItems} />
      {alert && (
          <AlertOverlay
              message={alert.message}
              variant={alert.variant}
              onClose={() => setAlert(null)}
          />
      )}
      <h2 className="mt-3">{getPageName('viewCargo')}</h2>
      <div className="mt-4">
        <div className="d-flex flex-column gap-2">
          <Row>
            <Col md={1}>
              <Form.Group>
                <Form.Label>{t('cargo.fields.dryIce')}</Form.Label>
                <Form.Check type="checkbox" checked={cargo?.dgr} readOnly />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>{t('cargo.fields.dangerousGoods')}</Form.Label>
                <Form.Check
                  type="checkbox"
                  checked={cargo?.dgrGoods}
                  readOnly
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>{t('cargo.fields.from')}</Form.Label>
                <Form.Control readOnly value={cargo?.from.name} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>{t('cargo.fields.client')}</Form.Label>
                <Form.Control readOnly value={cargo?.client.name} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>{t('cargo.fields.supplier')}</Form.Label>
                <Form.Control readOnly value={cargo?.supplier.name} />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={2}>
              <Form.Group>
                <Form.Label>{t('cargo.fields.pu')}</Form.Label>
                <Form.Control
                  readOnly
                  value={
                    cargo?.pu
                      ? new Date(cargo.pu).toLocaleDateString('pt-BR')
                      : ''
                  }
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>{t('cargo.fields.dawb')}</Form.Label>
                <Form.Control readOnly value={cargo?.dawb} />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>{t('cargo.fields.arrival')}</Form.Label>
                <Form.Control
                  readOnly
                  value={
                    cargo?.arrivalDate
                      ? new Date(cargo.arrivalDate).toLocaleDateString('pt-BR')
                      : ''
                  }
                />
              </Form.Group>
            </Col>
            <Col md={1}>
              <Form.Group>
                <Form.Label>{t('cargo.fields.pieces')}</Form.Label>
                <Form.Control readOnly value={cargo?.pieces} />
              </Form.Group>
            </Col>
            <Col md={1}>
              <Form.Group>
                <Form.Label>{t('cargo.fields.wgt')}</Form.Label>
                <Form.Control readOnly value={cargo?.weightRaw ?? cargo?.weight} />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>{t('cargo.fields.includedBy')}</Form.Label>
                <Form.Control readOnly value={cargo?.user.fullName} />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>{t('cargo.fields.inclusionDate')}</Form.Label>
                <Form.Control
                  readOnly
                  value={
                    cargo?.input
                      ? new Date(cargo.input).toLocaleDateString('pt-BR')
                      : ''
                  }
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>{t('cargo.fields.poRo')}</Form.Label>
                <Form.Control readOnly value={cargo?.poNumber} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>{t('cargo.fields.soNumber')}</Form.Label>
                <Form.Control readOnly value={cargo?.soNumber} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>{t('cargo.fields.wrNumber')}</Form.Label>
                <Form.Control readOnly value={cargo?.wrNumber} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>{t('cargo.fields.remarks')}</Form.Label>
                <Form.Control readOnly value={cargo?.description} />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <div className="mb-2">{t('cargo.fields.dimensions')}</div>
              <Table bordered>
                <thead>
                  <tr>
                    <th>{t('cargo.fields.quantityOfPieces')}</th>
                    <th>{t('cargo.fields.length')}</th>
                    <th>{t('cargo.fields.width')}</th>
                    <th>{t('cargo.fields.high')}</th>
                  </tr>
                </thead>
                <tbody>
                  {cargo?.dimensions?.length ? (
                    cargo.dimensions.map((dimension, index) => (
                      <tr key={index}>
                        <td>{dimension.quantity}</td>
                        <td>{dimension.length}</td>
                        <td>{dimension.width}</td>
                        <td>{dimension.height}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center">
                        {getUiInterface('noAvailableDimensions')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Col>
            <Col md={6}>
              <div className="mb-2">{t('cargo.fields.docs')}</div>
              <Table bordered>
                <thead>
                  <tr>
                    <th>{t('cargo.fields.invoice')}</th>
                    <th>{t('cargo.fields.documentType')}</th>
                    <th>{t('cargo.fields.content')}</th>
                  </tr>
                </thead>
                <tbody>
                  {cargo?.cargoContents?.length ? (
                    cargo.cargoContents.map((cargoContent, index) => (
                      <tr key={index}>
                        <td>{cargo?.carrierInformations[0]?.invoice}</td>
                        <td>{cargo?.carrierInformations[0]?.document?.key}</td>
                        <td>
                          {cargoContent?.content?.description ? (
                            <Box onClick={() => download(cargoContent.content)}
                              component="span"
                              title={t('cargo.labels.clickToDownload')}
                              sx={{
                                  cursor: "pointer",
                                  textDecoration: 'underline',
                                  '&:hover': {
                                    backgroundColor: '#f0f0f0',
                                    color: "blue"
                                  },
                              }}
                          >
                            {cargoContent.content.description}
                          </Box>
                          ) : (
                            <span>—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="text-center">
                        {getUiInterface('noAvailableInvoices')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

            </Col>
          </Row>
        </div>
      </div>

      <div className="d-flex gap-2 align-items-center mt-3 mb-3">
        <Button
          variant="primary"
          className="d-flex align-items-center gap-2"
          onClick={() => navigate(`edit`, { state: cargo })}
        >
          <span className="bi bi-pencil" />
          <span>{getUiInterface('edit')}</span>
        </Button>

        <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
          {getUiInterface('delete')}
        </Button>
      </div>

      <ConfirmDeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
      />
    </Container>
  );
};
